import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import Loader from "../loader/Loader";
import { downloadFile } from "../../../services/fileServices/FileService";
import {
  getAllSignatureRequests,
  resendSignatureRequest,
  deleteSignatureRequest,
  cancelSignatureRequestById,
} from "../../../services/fileServices/signatures/SignatureService";
import { toast } from "react-toastify";
import Form from "react-bootstrap/Form";
import { Spinner } from "react-bootstrap";
import searchicon from "../../../assets/images/searchicon.svg";
import tableeyeopenicon from "../../../assets/images/tableeyeopenicon.svg";
import tabledownloadicon from "../../../assets/images/tabledownloadicon.svg";
import tableresendbtnicon from "../../../assets/images/tableresendbtnicon.svg";
import { AllSignatureRequestResDto, SignatureRequestQueryReq } from "../../../interfaces/file/fileSignature/SignatureRequest";
import moment from "moment";
import { Download, Eye, Send, Trash, Trash2 } from "lucide-react";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import { convertUtcToLocalDate } from "../../../utilities/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@fortawesome/free-solid-svg-icons";

// type SignatureItem = {
//   fileId: string;
//   fileName: string;
//   signStatus: "pending" | "completed" | "canceled" | "rejected";
//   taskName: string;
//   taskId: number;
//   signedAt?: string;
//   createdAt: string;
//   signatureReqId: string;
//   signatureBoxId?: string;
//   emails: string[];
//   pages?: string;
// };

interface PaginationDto {
  pageNumber: number;
  pageSize: number;
}

interface SendSignDocumentRequestTableProps {
  searchTerm: string;
  statusFilter: string;
  startDate: Date | null;
  endDate: Date | null;
  pagination: PaginationDto;
  onTotalCountChange: (count: number) => void;
  onLoadingChange?: (loading: boolean) => void;
}

// TEMPORARY STATIC DATA FOR TESTING
// const STATIC_DATA: SignatureItem[] = [
//   {
//     fileId: "1",
//     fileName: "teshisd (1).pdf",
//     signStatus: "pending",
//     taskName: "OAAS Test",
//     taskId: 12345,
//     signedAt: "",
//     createdAt: "2025-12-02T04:03:07Z",
//     signatureReqId: "sig-1",
//     emails: ["test@example.com"],
//     pages: "610",
//   },
//   {
//     fileId: "2",
//     fileName: "contract_document.pdf",
//     signStatus: "completed",
//     taskName: "Legal Review",
//     taskId: 12346,
//     signedAt: "2025-12-15T10:30:00Z",
//     createdAt: "2025-12-01T08:15:00Z",
//     signatureReqId: "sig-2",
//     emails: ["legal@example.com"],
//     pages: "45",
//   },
//   {
//     fileId: "3",
//     fileName: "agreement_final.pdf",
//     signStatus: "pending",
//     taskName: "Client Agreement",
//     taskId: 12347,
//     signedAt: "",
//     createdAt: "2025-12-10T14:20:00Z",
//     signatureReqId: "sig-3",
//     emails: ["client@example.com"],
//     pages: "120",
//   },
//   {
//     fileId: "4",
//     fileName: "invoice_2024.pdf",
//     signStatus: "completed",
//     taskName: "Finance Approval",
//     taskId: 12348,
//     signedAt: "2025-12-20T16:45:00Z",
//     createdAt: "2025-12-05T09:00:00Z",
//     signatureReqId: "sig-4",
//     emails: ["finance@example.com"],
//     pages: "8",
//   },
//   {
//     fileId: "5",
//     fileName: "proposal_v3.pdf",
//     signStatus: "canceled",
//     taskName: "Project Proposal",
//     taskId: 12349,
//     signedAt: "",
//     createdAt: "2025-11-28T11:30:00Z",
//     signatureReqId: "sig-5",
//     emails: ["manager@example.com"],
//     pages: "230",
//   },
// ];

export default function SendSignDocumentRequestTable({
  searchTerm: externalSearchTerm,
  statusFilter: externalStatusFilter,
  startDate,
  endDate,
  pagination,
  onTotalCountChange,
  onLoadingChange,
}: SendSignDocumentRequestTableProps) {
  const navigate = useNavigate();
  const { currentUser, currentOrganization } = useAuth();
  const userId = (currentUser as any)?.uid;
  const token = (currentUser as any)?.accessToken;
  const [allDocuments, setAllDocuments] = useState<AllSignatureRequestResDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState(externalSearchTerm);
  const [sort, setSort] = useState<{ column: string; order: "asc" | "desc" }>({
    column: "created_date",
    order: "desc",
  });

  const handleDeleteClick = async (signatureBoxId: string) => {
    if (!signatureBoxId) {
      toast.error("Signature box ID is missing. Cannot cancel.");
      return;
    }

    const isConfirm = await showConfirmationDialog(
      "Cancel Signature Request?",
      "This will cancel the signature request and unlock the document. This cannot be undone.",
      "Yes, cancel it",
      "Cancel"
    );

    if (!isConfirm) return;

    try {
      setLoading(true);
      onLoadingChange?.(true);

      const result = await cancelSignatureRequestById(token, signatureBoxId);

      if (result.success) {
        // Extract proper message from result
        let successMessage = "Signature request cancelled successfully!";
        if (result.message) {
          // Try to parse if it's a JSON string
          try {
            const parsed = JSON.parse(result.message);
            // Extract data field (contains the actual message) or fall back to message field
            successMessage = parsed.data || parsed.message || successMessage;
          } catch {
            // If not JSON, use the string directly
            successMessage = result.message;
          }
        }
        toast.success(successMessage);
        fetchDocuments(); // Refresh the list
      } else {
        // Extract proper message from result
        let errorMessage = "Failed to cancel signature request";
        if (result.message) {
          // Try to parse if it's a JSON string
          try {
            const parsed = JSON.parse(result.message);
            errorMessage = parsed.data || parsed.message || parsed.error || errorMessage;
          } catch {
            // If not JSON, use the string directly
            errorMessage = result.message;
          }
        }
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error cancelling signature request:", error);
      // Extract proper error message from error object
      let errorMessage = "Failed to cancel signature request";
      if (error?.message) {
        errorMessage = typeof error.message === 'string' ? error.message : errorMessage;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(externalSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [externalSearchTerm]);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line
  }, [debouncedSearchTerm, externalStatusFilter, sort, pagination.pageNumber, pagination.pageSize, startDate, endDate]);

  async function fetchDocuments() {
    if (!currentUser) return;
    setLoading(true);
    onLoadingChange?.(true);
    try {
      // USING STATIC DATA FOR NOW - COMMENT THIS BLOCK WHEN REVERTING
      // setAllDocuments(STATIC_DATA);
      // setLoading(false);
      // return;
      // END STATIC DATA BLOCK

      // Normalize status filter for API
      const normalizeStatusForAPI = (status: string): string | null => {
        if (!status || status === "All Status") {
          return null;
        }
        // Map dropdown values to API values
        const statusMap: Record<string, string> = {
          "Pending": "pending",
          "Completed": "completed",
          "Rejected": "canceled", // Assuming "Rejected" maps to "canceled"
        };
        return statusMap[status] || status.toLowerCase();
      };

      const payload: SignatureRequestQueryReq = {
        ownerId: userId,
        searchTerm: debouncedSearchTerm,
        signStatus: normalizeStatusForAPI(externalStatusFilter),
        pageNumber: pagination.pageNumber,
        pageSize: pagination.pageSize,
        sort: sort.column,
        sortOrder: sort.order,
      };
      const result = await getAllSignatureRequests(token, payload);
      if (result) {
        setAllDocuments(result.items || []);
        // Update total count from backend
        onTotalCountChange(result.totalCount || 0);
      }
    } catch (error) {
      console.error("Error in fetchDocuments:", error);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }

  // Filter documents by date range (client-side since API doesn't support it)
  const filterDocumentsByDateRange = (
    docs: AllSignatureRequestResDto[]
  ): AllSignatureRequestResDto[] => {
    if (!startDate && !endDate) return docs;

    return docs.filter((doc) => {
      if (!doc.createdAt) return false;

      try {
        const createdDate = new Date(doc.createdAt);
        createdDate.setHours(0, 0, 0, 0);

        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return createdDate >= start && createdDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return createdDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return createdDate <= end;
        }
      } catch (error) {
        console.error("Error parsing date:", doc.createdAt);
        return false;
      }

      return true;
    });
  };

  // Apply date range filter (status and search are handled by backend)
  const filteredDocuments = filterDocumentsByDateRange(allDocuments);

  // Use filtered documents directly (pagination is handled by backend)
  const paginatedDocuments = filteredDocuments;

  const handleDownload = async (fileId: string, fileName: string) => {
    try {
      setLoading(true);
      if (currentUser) {
        const blob = await downloadFile(fileId, token);
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName || "document.pdf";
          document.body.appendChild(a);
          a.click();
          URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (signatureReqId: string) => {
    if (!signatureReqId || !currentUser) return;
    setLoading(true);
    onLoadingChange?.(true);
    try {
      const response = await resendSignatureRequest(token, signatureReqId);
      if (response) {
        toast.success("Send Request successfully!");
      }
    } catch (error) {
      console.error("Error resending signature request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    setSort((prev) => {
      if (prev.column === column) {
        return { column, order: prev.order === "asc" ? "desc" : "asc" };
      }
      return { column, order: "asc" };
    });
  };

  const navigateToTask = (friendlyId: number) => {
    navigate(`/task/${currentOrganization?.abbreviation}-${friendlyId}`);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="signature-table-wrapper">
        {/* Table Section */}
        <div className="signature-table-container">
          <table className="drive-table min-w-[1100px] table" style={{ tableLayout: 'fixed' }}>
            <thead className="table-header-css">
              <tr>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("fileName")}
                >
                  FILE NAME
                  <span className="sort-icon">⇅</span>
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("taskName")}
                >
                  TASK NAME
                  <span className="sort-icon">⇅</span>
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("taskId")}
                >
                  TASK ID
                  <span className="sort-icon">⇅</span>
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("signStatus")}
                >
                  STATUS
                  <span className="sort-icon">⇅</span>
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("createdAt")}
                >
                  CREATED DATE
                  <span className="sort-icon">⇅</span>
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("signedAt")}
                >
                  SIGNED AT
                  <span className="sort-icon">⇅</span>
                </th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody
              className={paginatedDocuments.length >= 10 && pagination.pageSize === 10 ? "page-size-10" : ""}
            >
              {loading ? (
                <tr>
                  <td colSpan={7} className="no-records">
                    <Spinner animation="border" size="sm" /> Loading...
                  </td>
                </tr>
              ) : paginatedDocuments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-records">
                    No records found.
                  </td>
                </tr>
              ) : (
                paginatedDocuments.map((doc, index) => (
                  <tr
                    key={index}
                    className={`signature-table-row ${pagination.pageSize === 10 ? "row-height-10" : ""
                      }`}
                    onClick={() => {
                      if (doc.friendlyId) {
                        navigateToTask(doc.friendlyId)
                      }
                    }}
                  >
                    <td>
                      <div className="file-name-cell">
                        <div className="file-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="17"
                            height="17"
                            viewBox="0 0 17 17"
                            fill="none"
                          >
                            <path
                              d="M10.1056 1.34729H4.04264C3.68531 1.34729 3.34261 1.48924 3.08994 1.74191C2.83726 1.99459 2.69531 2.33729 2.69531 2.69462V13.4733C2.69531 13.8306 2.83726 14.1733 3.08994 14.426C3.34261 14.6787 3.68531 14.8206 4.04264 14.8206H12.1266C12.484 14.8206 12.8267 14.6787 13.0793 14.426C13.332 14.1733 13.474 13.8306 13.474 13.4733V4.71562L10.1056 1.34729Z"
                              stroke="#8B5CF6"
                              strokeWidth="1.0105"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.42969 1.34729V4.04195C9.42969 4.39929 9.57164 4.74199 9.82431 4.99466C10.077 5.24733 10.4197 5.38928 10.777 5.38928H13.4717"
                              stroke="#8B5CF6"
                              strokeWidth="1.0105"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.736 6.06299H5.38867"
                              stroke="#8B5CF6"
                              strokeWidth="1.0105"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.778 8.75769H5.38867"
                              stroke="#8B5CF6"
                              strokeWidth="1.0105"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.778 11.4523H5.38867"
                              stroke="#8B5CF6"
                              strokeWidth="1.0105"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="document-filename shortDescription" title={doc.fileName}>
                          {doc.fileName}
                        </span>
                      </div>
                    </td>
                    <td className="shortDescription" title={doc.taskName}>{doc.taskName}</td>
                    <td className="document-table-cell">
                      {doc.friendlyId ? `${currentOrganization?.abbreviation}-${doc.friendlyId}` : "-"}
                    </td>
                    <td>
                      <span
                        className={`status-badge status-badge-new status-${doc.signStatus}`}
                      >
                        {doc.signStatus.charAt(0).toUpperCase() +
                          doc.signStatus.slice(1)}
                      </span>
                    </td>
                    <td className="document-table-cell shortDescription" title={doc.createdAt
                        ? (() => {
                          const localDate = convertUtcToLocalDate(doc.createdAt);
                          return localDate
                            ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                            : "-";
                        })()
                        : "-"}>
                      {doc.createdAt
                        ? (() => {
                          const localDate = convertUtcToLocalDate(doc.createdAt);
                          return localDate
                            ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                            : "-";
                        })()
                        : "-"}
                    </td>
                    <td className="document-table-cell shortDescription" title={doc.signStatus === "pending"
                        ? "-"
                        : doc.signedAt
                          ? (() => {
                            const localDate = convertUtcToLocalDate(doc.signedAt);
                            return localDate
                              ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                              : "-";
                          })()
                          : "-"}>
                      {doc.signStatus === "pending"
                        ? "-"
                        : doc.signedAt
                          ? (() => {
                            const localDate = convertUtcToLocalDate(doc.signedAt);
                            return localDate
                              ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                              : "-";
                          })()
                          : "-"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action_btn">
                        <span>
                          <Eye
                            onClick={() => navigate(`/view/file/${doc.fileId}`, {
                              state: { fromSignDocument: true }
                            })}
                          />
                        </span>
                        <span>
                          {doc.signStatus === "completed" ? (
                            <Download
                              onClick={() =>
                                handleDownload(doc.fileId, doc.fileName)
                              }
                            />
                          ) : (
                            <>
                              {
                                doc.signStatus == "pending" &&
                                <Send
                                  onClick={() => handleSendRequest(doc.fileId)}
                                />
                              }
                            </>
                          )}
                        </span>
                        <span>
                          {(doc.signStatus != 'completed' && doc.signStatus != 'rejected' && doc.signStatus != 'canceled') && (
                            <>
                              <FontAwesomeIcon icon={faBan}
                                onClick={() => {
                                  if (doc.signatureBoxId) {
                                    handleDeleteClick(doc.signatureBoxId);
                                  }
                                }}
                              />
                            </>
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
